import { Beaker, Info } from 'lucide-react';
import { amountForTank, amountForJob, formatAmount } from '@/utils/sprayDose';

export interface TankDoseProduct {
  key: string | number;
  name: string;
  /** The dose actually applied, in `rateUnit` — any concentration already in it. */
  rate: number | null;
  rateUnit: string;
}

interface TankDoseTableProps {
  products: TankDoseProduct[];
  tankCapacityL: number | null | undefined;
  outputRateLpha: number | null | undefined;
  /** Water for the whole job, so a part-filled last tank can be stated too. */
  totalWaterL: number | null;
  /** Ground the job covers (coverage applied) — what a per-hectare rate totals over. */
  areaHa?: number | null;
  title?: string;
}

const litres = (n: number) => `${Math.round(n).toLocaleString('en-AU')} L`;

/**
 * What goes into each tank, product by product, and what the whole job takes.
 *
 * This is the number somebody stands at the tank with. A job total and a count
 * of fills leaves them doing the division in the shed, and a mix partner had
 * no amount at all. The last tank is stated separately when it is a part fill,
 * because a full-tank dose in half a tank of water is double strength. The
 * job total sits beside it so the stock to pull from the shed is one glance.
 */
function TankDoseTable({
  products, tankCapacityL, outputRateLpha, totalWaterL, areaHa, title = 'What goes in each tank',
}: TankDoseTableProps) {
  if (!products.length) return null;

  const capacity = tankCapacityL && tankCapacityL > 0 ? tankCapacityL : null;
  const water = totalWaterL && totalWaterL > 0 ? totalWaterL : null;
  const area = areaHa && areaHa > 0 ? areaHa : null;

  const fills = capacity && water ? Math.ceil(water / capacity) : null;
  const lastWater = fills && capacity && water ? water - (fills - 1) * capacity : null;
  // Within a litre of full is a full tank; nobody fills to the litre.
  const partialLast = fills != null && capacity && lastWater != null && capacity - lastWater >= 1 ? lastWater : null;
  const fullTanks = fills == null ? null : partialLast != null ? fills - 1 : fills;

  const rows = products.map(p => ({
    ...p,
    full: capacity
      ? amountForTank({ rate: p.rate, rateUnit: p.rateUnit, waterL: capacity, outputRateLpha })
      : null,
    last: partialLast != null
      ? amountForTank({ rate: p.rate, rateUnit: p.rateUnit, waterL: partialLast, outputRateLpha })
      : null,
    job: amountForJob({ rate: p.rate, rateUnit: p.rateUnit, areaHa: area, waterL: water }),
  }));

  const showTank = capacity != null;
  const showJob = rows.some(r => r.job != null);
  if (!showTank && !showJob) return null;

  const anyTankUnknown = showTank && rows.some(r => r.full == null);
  const cols = showTank && showJob
    ? 'grid-cols-[minmax(0,1fr)_auto_auto]'
    : 'grid-cols-[minmax(0,1fr)_auto]';
  const amountCell = (a: { amount: number; unit: string } | null) => (
    <p className={`text-right font-semibold ${a ? 'text-green-800' : 'text-neutral-400'}`}>
      {a ? formatAmount(a.amount, a.unit) : '—'}
    </p>
  );

  const facts: { label: string; value: string }[] = [];
  if (area) facts.push({ label: 'Area', value: `${area.toFixed(2)} ha` });
  if (outputRateLpha && outputRateLpha > 0) facts.push({ label: 'Water rate', value: `${Math.round(outputRateLpha).toLocaleString('en-AU')} L/ha` });
  if (water) facts.push({ label: 'Total water', value: litres(water) });
  if (capacity) facts.push({ label: 'Tank size', value: litres(capacity) });
  if (fills != null) {
    facts.push({
      label: 'Tanks',
      value: partialLast != null
        ? `${fills} (${fullTanks} full + 1 part)`
        : String(fills),
    });
  }

  return (
    <div className="rounded-xl border border-green-200 bg-white p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Beaker className="h-4 w-4 text-green-700 shrink-0" />
        <h3 className="text-sm font-semibold text-neutral-800">{title}</h3>
      </div>

      {facts.length > 0 && (
        <dl className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {facts.map(f => (
            <div key={f.label} className="rounded-lg bg-neutral-50 px-3 py-2 min-w-0">
              <dt className="text-xs text-neutral-500">{f.label}</dt>
              <dd className="text-sm font-semibold text-neutral-800 break-words">{f.value}</dd>
            </div>
          ))}
        </dl>
      )}

      <div className={`grid ${cols} gap-x-4 gap-y-1.5 text-sm`}>
        <p className="text-xs text-neutral-500">Product</p>
        {showTank && (
          <p className="text-xs text-neutral-500 text-right">
            Per full tank
            <span className="block">{litres(capacity!)} water</span>
          </p>
        )}
        {showJob && (
          <p className="text-xs text-neutral-500 text-right">
            Whole job
            {area && <span className="block">{area.toFixed(2)} ha</span>}
          </p>
        )}
        {rows.map(r => (
          <div key={r.key} className="contents">
            <p className="text-neutral-700 break-words min-w-0">{r.name}</p>
            {showTank && amountCell(r.full)}
            {showJob && amountCell(r.job)}
          </div>
        ))}
      </div>

      {partialLast != null && (
        <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1.5 text-sm pt-2 border-t border-neutral-100">
          <p className="text-xs text-neutral-500">Last tank — {litres(partialLast)} water</p>
          <span />
          {rows.map(r => (
            <div key={r.key} className="contents">
              <p className="text-neutral-700 break-words min-w-0">{r.name}</p>
              {amountCell(r.last)}
            </div>
          ))}
        </div>
      )}

      {anyTankUnknown && (
        <p className="text-xs text-amber-700 flex items-start gap-1">
          <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          {outputRateLpha
            ? 'A product with no rate set has no amount — give it a rate.'
            : 'A per-hectare rate needs the sprayer\'s calibrated output rate (L/ha) to turn a tank of water into an amount of product. Calibrate the sprayer to fill these in.'}
        </p>
      )}
    </div>
  );
}

export default TankDoseTable;
